import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Check } from 'lucide-react';
import {
  fetchOnlineReportInput, buildChannels, downloadEditorialOnlinePdf,
} from '../utils/onlineReportEditorialPdf';

/**
 * OnlineExportReportModal
 * ───────────────────────
 * Same UX as StockExportReportModal: PDF / Excel format toggle + selectable
 * sections (here, the online CHANNELS discovered from the data) + export.
 * PDF = the channel-separated editorial report; Excel = channel data sheets.
 */
const round = (v) => Math.round(Number(v) || 0);
const gpOf = (c) => (c.gpKnown === false ? '' : round(c.grossProfit));
const marginOf = (c) => (c.gpKnown === false || !(c.gpMeasurableNet > 0) ? '' : Number((c.margin || 0).toFixed(1)));
const itemsText = (n) => `excludes ${n} item${n === 1 ? '' : 's'} with unknown cost`;

/**
 * The Excel Overview sheet. Its GP Note is a CELL on each row (C10): a sheet
 * gets filtered, sorted and pasted, and a note at the bottom is lost first.
 */
export function buildOverviewRows(chs, tot, totMargin) {
  const noteOf = (c) => {
    if (c.gpKnown === false) return 'cost unknown';
    return c.gpExcludes > 0 ? itemsText(c.gpExcludes) : '';
  };
  const excludedItems = chs.reduce((n, c) => n + (c.gpKnown === false ? 0 : Number(c.gpExcludes) || 0), 0);
  const unknownChannels = chs.filter((c) => c.gpKnown === false && c.netSales > 0).length;
  const totalNote = [
    excludedItems > 0 ? itemsText(excludedItems) : null,
    unknownChannels > 0 ? `excludes ${unknownChannels} channel${unknownChannels === 1 ? '' : 's'} with unknown cost` : null,
  ].filter(Boolean).join('; ');
  return [
    ['Channel', 'Orders', 'Net Sales', 'AOV', 'Discount %', 'Commission %', 'Net Payout', 'Gross Profit', 'Margin %', 'GP Note'],
    ...chs.map(c => [
      c.name, c.orders, round(c.netSales), c.orders > 0 ? round(c.aov) : '',
      c.grossSales > 0 ? Number((c.discountPct || 0).toFixed(1)) : '',
      c.grossSales > 0 ? Number((c.commissionPct || 0).toFixed(1)) : '',
      round(c.netPayout), gpOf(c), marginOf(c), noteOf(c),
    ]),
    ['All Channels', tot.orders, round(tot.netSales), tot.orders > 0 ? round(tot.netSales / tot.orders) : '',
      '', '', round(tot.netPayout), round(tot.grossProfit), totMargin, totalNote],
  ];
}

const OnlineExportReportModal = ({ isOpen, onClose, periodStart, periodEnd, periodLabel = '' }) => {
  const [exportFormat, setExportFormat] = useState('pdf');
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [input, setInput] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    setExportDone(false);
    setLoadError(false);
    setLoading(true);
    (async () => {
      try {
        const data = await fetchOnlineReportInput({ start: periodStart, end: periodEnd, label: periodLabel });
        if (!alive) return;
        const chs = buildChannels(data)
          .filter(c => c.orders > 0 || c.grossSales > 0 || (c.items && c.items.length))
          .sort((a, b) => { if (a.key === 'others') return 1; if (b.key === 'others') return -1; return b.netSales - a.netSales; });
        setInput(data);
        setChannels(chs);
        setSelectedKeys(chs.map(c => c.key));
      } catch (e) {
        console.error('Online export: failed to load data', e);
        if (alive) setLoadError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isOpen, periodStart, periodEnd, periodLabel]);

  if (!isOpen) return null;

  const fileTimestamp = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

  const toggleChannel = (key) => {
    setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const toggleAll = () => {
    setSelectedKeys(prev => prev.length === channels.length ? [] : channels.map(c => c.key));
  };

  // Restrict the fetched payloads to the selected channels.
  const filteredInput = () => {
    const sel = new Set(selectedKeys.map(k => k.toLowerCase()));
    const platformBreakdown = {};
    Object.entries(input.platformBreakdown || {}).forEach(([k, v]) => { if (sel.has(k.toLowerCase())) platformBreakdown[k] = v; });
    const items = (input.items || []).filter(it => sel.has(String(it.channel || it.platform || 'others').toLowerCase()));
    const orders = (input.orders || []).filter(o => sel.has(String(o.platform || o.platform_source || 'others').toLowerCase()));
    return { ...input, platformBreakdown, items, orders };
  };

  const exportPDF = async () => {
    await downloadEditorialOnlinePdf(filteredInput(), `Online_Report_${fileTimestamp}.pdf`);
  };

  const exportExcel = async () => {
    const sel = filteredInput();
    const chs = buildChannels(sel)
      .filter(c => c.hasActivity || c.fromMaster)
      .sort((a, b) => { if (a.key === 'others') return 1; if (b.key === 'others') return -1; return (b.netSales || 0) - (a.netSales || 0); });
    const tot = chs.reduce((a, c) => ({
      orders: a.orders + c.orders, netSales: a.netSales + c.netSales, discount: a.discount + c.discount,
      commission: a.commission + c.commission, netPayout: a.netPayout + c.netPayout,
      grossProfit: a.grossProfit + (c.gpKnown === false ? 0 : c.grossProfit),
      gpMeasurableNet: a.gpMeasurableNet + (c.gpKnown === false ? 0 : c.gpMeasurableNet),
    }), { orders: 0, netSales: 0, discount: 0, commission: 0, netPayout: 0, grossProfit: 0, gpMeasurableNet: 0 });
    // Margin over the sales of the dishes whose cost is known — the ones the
    // profit includes.
    const totMargin = tot.gpMeasurableNet > 0 ? Number((tot.grossProfit / tot.gpMeasurableNet * 100).toFixed(1)) : 0;

    const wb = XLSX.utils.book_new();

    // ── Overview sheet (channel comparison — §4.3 columns) ──
    const overview = buildOverviewRows(chs, tot, totMargin);
    const ovWs = XLSX.utils.aoa_to_sheet(overview);
    ovWs['!cols'] = [{ wch: 16 }, { wch: 8 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 9 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ovWs, 'Overview');

    // ── One sheet per channel (§7): name = channel.name, ≤31 chars, deduped ──
    const usedNames = new Set(['Overview']);
    const safeSheetName = (name) => {
      let base = String(name || 'Channel').replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || 'Channel';
      let candidate = base, n = 2;
      while (usedNames.has(candidate)) { const suffix = ` (${n++})`; candidate = base.slice(0, 31 - suffix.length) + suffix; }
      usedNames.add(candidate);
      return candidate;
    };

    chs.forEach(c => {
      const rows = [
        [c.name],
        [],
        ['Summary'],
        ['Gross Sales', round(c.grossSales)],
        ['Discount', round(c.discount)],
        ['Net Sales', round(c.netSales)],
        ['Commission', round(c.commission)],
        ['Net Payout', round(c.netPayout)],
        ['Orders', c.orders],
        ['Avg Order Value', c.orders > 0 ? round(c.aov) : ''],
        ['Gross Profit', gpOf(c)],
        ['Margin %', marginOf(c)],
        ...(c.gpExcludes > 0 ? [['Note', `Gross profit and margin exclude ${c.gpExcludes} item(s) whose cost isn't known`]] : []),
      ];
      if (c.items && c.items.length) {
        rows.push([], ['Items'], ['Item', 'Qty', 'Net Sales', 'Gross Profit', 'Margin %', 'Cost Basis']);
        c.items.forEach(it => {
          const mg = it.approximate ? '' : (it.netSales > 0 ? Number((it.grossProfit / it.netSales * 100).toFixed(1)) : 0);
          // C10. The mark is a CELL on the row, not only a note at the bottom: a spreadsheet
          // gets filtered, sorted and pasted elsewhere, and the note is the first thing lost.
          rows.push([it.name, it.qty != null ? it.qty : '', round(it.netSales), it.approximate ? '' : round(it.grossProfit), mg,
            it.approximate ? 'unavailable' : (it.estimated ? '~ estimated' : '')]);
        });
        const estRows = c.items.filter((it) => it && it.estimated && !it.approximate);
        if (estRows.length) {
          const estTotal = estRows.reduce((sum, it) => sum + (Number(it.estimatedCost) || 0), 0);
          rows.push([], [`~ Estimated. ${estRows.length} item(s) include ingredients priced at their last known `
            + `purchase price because stock was unavailable and no confirmed closing-stock price exists. `
            + `Estimated cost included: ₹${estTotal.toFixed(2)}.`]);
        }
      }
      if (c.daily && c.daily.length) {
        rows.push([], ['Daily Breakdown'], ['Date', 'Orders', 'Net Sales', 'Discount', 'Net Payout']);
        c.daily.forEach(d => rows.push([d.date, d.orders, round(d.netSales), round(d.discount), round(d.netPayout)]));
      }
      if (!c.hasActivity) rows.push([], [`No ${c.name} orders in this period.`]);
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 24 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName(c.name));
    });

    if (wb.SheetNames.length === 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['No data available for selected channels']]), 'Info');
    }
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `Online_Report_${fileTimestamp}.xlsx`);
  };

  const handleExport = async () => {
    if (isExporting || !selectedKeys.length) return;
    setIsExporting(true);
    try {
      if (exportFormat === 'pdf') await exportPDF();
      else await exportExcel();
      setExportDone(true);
    } catch (err) {
      console.error('Online export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ─── Done Screen ──────────────────────────────────────
  if (exportDone) {
    return (
      <div style={overlayStyle} onClick={() => { setExportDone(false); onClose(); }}>
        <div style={modalStyle} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', border: '3px solid #22c55e', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Check size={36} color="#22c55e" strokeWidth={3} />
              </div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Report Downloaded!</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Your {exportFormat === 'pdf' ? 'PDF' : 'Excel (.xlsx)'} has successfully downloaded.
            </div>
            <button onClick={() => { setExportDone(false); onClose(); }} style={primaryBtnStyle}>OK</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Modal ──────────────────────────────────────
  return (
    <div style={overlayStyle} onClick={() => { if (!isExporting) onClose(); }}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1f2937' }}>Export Online Report</h3>
          <button onClick={() => { if (!isExporting) onClose(); }} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>&times;</button>
        </div>

        {/* Format Toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { key: 'pdf', label: 'PDF', desc: 'Channel Report' },
            { key: 'excel', label: 'Excel', desc: 'Data Sheets (.xlsx)' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setExportFormat(f.key)}
              style={{
                flex: 1, padding: '12px 8px', borderRadius: 10,
                border: `2px solid ${exportFormat === f.key ? '#000' : '#e5e7eb'}`,
                background: exportFormat === f.key ? '#f3f4f6' : '#fff',
                cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: exportFormat === f.key ? '#000' : '#374151' }}>{f.label}</div>
              <div style={{ fontSize: 11, color: exportFormat === f.key ? '#374151' : '#9ca3af', marginTop: 2 }}>{f.desc}</div>
            </button>
          ))}
        </div>

        {/* Channel Selection */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Select Channels to Export</span>
            {channels.length > 0 && (
              <button onClick={toggleAll} style={{ background: 'none', border: 'none', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {selectedKeys.length === channels.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ padding: '18px 12px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Loading channels…</div>
          ) : loadError ? (
            <div style={{ padding: '14px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12.5 }}>Could not load online report data. Please try again.</div>
          ) : channels.length === 0 ? (
            <div style={{ padding: '18px 12px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No online orders in this period.</div>
          ) : (
            channels.map(c => (
              <label key={c.key} style={checkboxRowStyle(selectedKeys.includes(c.key))}>
                <input type="checkbox" checked={selectedKeys.includes(c.key)} onChange={() => toggleChannel(c.key)} style={checkboxStyle} />
                <span style={{ fontSize: 13.5, fontWeight: 500, color: '#374151', flex: 1 }}>{c.name}</span>
                <span style={badgeStyle}>{`${(c.orders || 0).toLocaleString('en-IN')} orders`}</span>
              </label>
            ))
          )}
        </div>

        {/* Period Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', marginBottom: 16, background: '#f5f5f5', borderRadius: 8, fontSize: 12, color: '#6b7280' }}>
          <span>Period: <strong style={{ color: '#374151' }}>{periodLabel}</strong></span>
        </div>

        {/* Export Button */}
        <button
          onClick={handleExport}
          disabled={isExporting || loading || selectedKeys.length === 0}
          style={{
            ...primaryBtnStyle,
            width: '100%', padding: '14px 24px', fontSize: 15,
            opacity: (isExporting || loading || selectedKeys.length === 0) ? 0.6 : 1,
            cursor: (isExporting || loading || selectedKeys.length === 0) ? 'not-allowed' : 'pointer',
          }}
        >
          {isExporting
            ? (exportFormat === 'pdf' ? 'Generating PDF...' : 'Generating Excel...')
            : `Export as ${exportFormat === 'pdf' ? 'PDF' : 'Excel'} (${selectedKeys.length} channel${selectedKeys.length !== 1 ? 's' : ''})`}
        </button>
      </div>
    </div>
  );
};

// ─── Styles (identical to StockExportReportModal) ─────────────────────────
const overlayStyle = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 99999, padding: 16, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};
const modalStyle = {
  width: 'min(440px, 94vw)', background: '#fff', borderRadius: '0.5rem',
  boxShadow: '0 20px 60px rgba(0,0,0,0.18)', padding: '24px 20px', maxHeight: '88vh', overflowY: 'auto',
};
const primaryBtnStyle = {
  padding: '12px 24px', backgroundColor: '#000000', color: '#fff',
  border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const checkboxRowStyle = (isChecked) => ({
  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', marginBottom: 5, borderRadius: 10,
  background: isChecked ? '#f3f4f6' : '#f9fafb', border: `1.5px solid ${isChecked ? '#9ca3af' : '#e5e7eb'}`,
  cursor: 'pointer', transition: 'all 0.15s',
});
const checkboxStyle = { width: 17, height: 17, accentColor: '#000', cursor: 'pointer' };
const badgeStyle = { fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' };

export default OnlineExportReportModal;
